import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";

/**
 * Company Prospects — who we have approached about a vacancy they posted.
 *
 * The prospect list itself is not stored: it is `scraped_jobs` grouped by
 * employer, which is why it costs nothing and refreshes itself every weekday.
 * What cannot be derived is the half a person adds — that this employer was
 * emailed on Tuesday, that they replied, that they are not worth chasing. That
 * is all this table holds, one row per employer, created the first time someone
 * acts on one.
 *
 * Keyed on `employer_key` rather than `company_id`, and this is the point of the
 * table: a scraped offer has no `company_id` until somebody approves it, and
 * most never are. Keying on the resolved company would mean the employers we
 * have *not* published — the great majority, and the whole prospect list — could
 * not hold a status at all. `config/employer_identity.ts` derives the key from
 * the name, so it exists from the moment the offer is scraped.
 *
 * `company_id` is the reconciliation, added by 0044. It stays nullable and it
 * stays secondary: the majority of these employers have no `companies` row and
 * never will, so the key remains the identity and this is the pointer to the
 * brand record *when one exists*. Before it, the same employer could sit in both
 * tables under two spellings with nothing joining them — 10 of 32 already did —
 * so a company's own screen could show no correspondence while an outreach
 * thread with them was three months old.
 *
 * `source` is the other half of the same change: the scrape queue is no longer
 * the only door. An employer who wrote to us first — a demo request, the contact
 * form, a cold email — enters here directly, and sorts ahead of anything cold.
 *
 * Nothing here is public, and no end user has any reason to read it — these are
 * our notes about companies that have not asked us for anything. Access is
 * admin-only, which is also all the default injected policies grant.
 */
const companyProspectsCollection: CollectionConfig = {
    name: "Empresas a contactar",
    singularName: "Empresa a contactar",
    slug: "company-prospects",
    table: "company_prospects",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        employer_key: {
            name: "Clave de empresa",
            type: "string",
            validation: {
                required: true,
                unique: true
            },
            description: "Identidad normalizada del empleador (config/employer_identity.ts). No editar a mano: cambiarla desvincula el estado de las ofertas que lo produjeron.",
            admin: { readOnly: true }
        },
        employer_name: {
            name: "Empresa",
            type: "string",
            validation: {
                required: true
            },
            description: "Cómo aparece el empleador en sus ofertas"
        },
        employer_website: {
            name: "Sitio web",
            type: "string",
            description: "URL completa, tal como venía en la oferta"
        },
        company_id: {
            name: "Ficha de empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            description: "La misma empresa en el directorio, si tiene ficha. Vacío en la mayoría: sólo se crea una ficha cuando se aprueba una de sus ofertas."
        },
        source: {
            name: "De dónde salió",
            type: "string",
            enum: [
                { id: "scraped", label: "La vimos publicando empleo", color: "gray" },
                { id: "inbound", label: "Nos escribieron", color: "green" },
                { id: "manual", label: "Añadida a mano", color: "blue" }
            ],
            defaultValue: "scraped",
            description: "«Nos escribieron» va primero en la cola: una empresa que levanta la mano vale más que una que encontramos nosotros."
        },
        source_detail: {
            name: "Origen",
            type: "string",
            description: "Qué puerta concretamente, en palabras. Sólo para leer.",
            admin: { readOnly: true }
        },
        status: {
            name: "Estado",
            type: "string",
            enum: [
                // The order is the funnel, so a table sorted by this column
                // reads from "not started" to "done" rather than alphabetically.
                { id: "new", label: "Sin contactar", color: "gray" },
                { id: "contacted", label: "Contactada", color: "blue" },
                { id: "replied", label: "Respondió", color: "cyan" },
                { id: "meeting", label: "Reunión", color: "orange" },
                { id: "won", label: "Cliente", color: "green" },
                { id: "discarded", label: "Descartada", color: "red" }
            ],
            defaultValue: "new"
        },
        // ── Deprecated, kept until the column is dropped ─────────────
        //
        // Superseded by the `prospect_contacts` table, which holds as many
        // people per employer as there are. 0040 copied both of these into it
        // and nothing reads them any more; they remain because migrations are
        // applied before the deploy that stops reading them, so dropping them
        // in 0040 would have broken the running panel for the length of a
        // deploy. Drop them in the migration after the one that ships this.
        contact_name: {
            name: "Persona de contacto (obsoleto)",
            type: "string",
            description: "Sustituido por la lista de personas. No editar: nada lo lee.",
            admin: { readOnly: true }
        },
        contact_email: {
            name: "Email de contacto (obsoleto)",
            type: "string",
            description: "Sustituido por la lista de personas. No editar: nada lo lee.",
            admin: { readOnly: true }
        },
        owner: {
            name: "Asignada a",
            type: "string",
            description: "El email de la persona del equipo que trabaja esta empresa. Vacío = nadie la ha tomado. Ver drizzle/0054."
        },
        last_contacted_at: {
            name: "Último contacto",
            type: "date",
            description: "Se sella al marcar 'Contactada'"
        },
        next_action: {
            name: "Próximo paso",
            type: "string",
            description: "Qué toca hacer la próxima vez. Sin esto, una fecha de seguimiento obliga a abrir la ficha para saber por qué."
        },
        next_action_at: {
            name: "Fecha del próximo paso",
            type: "date",
            description: "Lo que convierte la lista en una cola: la pestaña 'Para hoy' se calcula con esta fecha."
        },
        contacts_synced_at: {
            name: "Personas buscadas",
            type: "date",
            description: "Cuándo se buscaron personas por última vez. Sin esto, 'no hay personas' significa a la vez 'nadie ha mirado' y 'miramos y no había'.",
            admin: { readOnly: true }
        },
        contacts_note: {
            name: "Resultado de la búsqueda",
            type: "string",
            description: "Qué encontró la última búsqueda, en palabras",
            admin: { readOnly: true }
        },
        notes: {
            name: "Notas",
            type: "string",
            admin: {
                multiline: true
            }
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        updated_at: {
            name: "Actualizado",
            type: "date",
            autoValue: "on_update"
        }
    },
    admin: {
        group: "Captación",
        icon: "ContactRound",
        // Not in `navigationGroupMappings`, like `scraped_jobs`: "Empresas que
        // contratan" is the screen this table is meant to be used from, and a
        // second door into the raw rows would be a second, worse version of it.
        sort: ["updated_at", "desc"],
        // Fuera del menú: esta tabla es el interior de "Empresas que contratan",
        // no una pantalla. Como entrada del menú era tres cosas confusas a la
        // vez — vacía hasta que alguien actúa, sin explicar qué es, y un
        // segundo camino peor hacia esa pantalla.
        //
        // `group` por sí solo NO la ocultaba: el menú muestra cualquier
        // colección que declare uno, esté o no en `navigationGroupMappings`.
        // Sigue siendo navegable — el lápiz de la pantalla abre la fila.
        hideFromNavigation: true
    }
};

export default companyProspectsCollection;
